import type { JSX } from 'react';

import { SYMBOLE, type Symbolname, type Symbolpfad } from '../symbole';

type Gewicht = 'light' | 'duotone';

/** Die Namen, die es in einem Gewicht gibt — aus `symbole.ts` abgeleitet. */
export type NamenVon<G extends Gewicht, S = Symbolname> = S extends `${infer N}:${G}` ? N : never;

/**
 * Ein Sinnbild für die Kachel im Seitenkopf: ein Duotone-Name, oder mit
 * `:light` dahinter ein Light-Zeichen (Berichtsseite, Auftrag CRM-54).
 */
export type Kachelsinnbild = NamenVon<'duotone'> | `${NamenVon<'light'>}:light`;

/** Die Kachel des Seitenkopfs — Light, wenn der Name es sagt. */
export function Kachelzeichen({ sinnbild }: { readonly sinnbild: Kachelsinnbild }): JSX.Element {
  return sinnbild.endsWith(':light') ? (
    <Sinnbild gewicht="light" name={sinnbild.slice(0, -':light'.length) as NamenVon<'light'>} />
  ) : (
    <Sinnbild name={sinnbild as NamenVon<'duotone'>} />
  );
}

/**
 * EIN SINNBILD AUS PHOSPHOR (ÄA-41, Entwurf v2).
 *
 * `Sinnbild` und nicht `Symbol`: Der Name verdeckte sonst das eingebaute
 * `Symbol` in jeder Datei, die die Komponente einbindet.
 *
 * Light im Rahmen (Kopfzeile, Menüzeile, Aufklapper), Duotone im Inhalt — so
 * setzt es der Entwurf. Ausnahme seit Auftrag CRM-54: Die Berichtsseite und die
 * Widgets der Arbeitsfläche zeichnen durchgehend Light (`gewicht="light"`),
 * denn die halbdurchsichtige Duotone-Fläche wirkte dort als grauer Fleck in
 * der Mitte. Welche Zeichen es gibt, steht in `symbole.ts`; ein
 * Name, der dort fehlt, ist ein Übersetzungsfehler und kein leeres Kästchen.
 *
 * NUR BILD (`aria-hidden`). Die Bedeutung trägt immer der Text daneben oder
 * die Beschriftung des Knopfs (`aria-label`, `title`) — ein Zeichen allein
 * sagt einem Vorleseprogramm nichts, und eine zweite Ansage desselben Worts
 * wäre Lärm (WCAG 1.1.1).
 *
 * Die Größe folgt der Schrift (`1em`), die Farbe der Schriftfarbe
 * (`currentColor`): Ein Zeichen im Knopf wird mit ihm hell, dunkel und groß.
 */
export function Sinnbild<G extends Gewicht = 'duotone'>({
  name,
  gewicht,
  klasse,
}: {
  readonly name: NamenVon<G>;
  readonly gewicht?: G;
  readonly klasse?: string;
}): JSX.Element {
  const pfade: readonly Symbolpfad[] = SYMBOLE[`${name}:${gewicht ?? 'duotone'}` as Symbolname];
  return (
    <svg
      className={klasse === undefined ? 'symbol' : `symbol ${klasse}`}
      viewBox="0 0 256 256"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      /* Welches Zeichen es ist — für Prüfungen und die Fehlersuche, nicht für die Anzeige. */
      data-sinnbild={`${name}:${gewicht ?? 'duotone'}`}
    >
      {pfade.map((p, i) => (
        <path key={i} d={p.d} {...(p.o === undefined ? {} : { opacity: p.o })} />
      ))}
    </svg>
  );
}
